import { useRef, useEffect, useImperativeHandle, forwardRef, useState } from 'react';

/**
 * Canvas signature pad. This is the one place a customer is legally binding
 * themselves, so the details matter:
 *
 *  - Pointer Events cover mouse, touch and stylus in one code path.
 *  - `touch-action: none` (in doc.css) stops iOS scrolling the page instead
 *    of drawing — without it the pad is unusable on a phone, which is where
 *    most customers will sign.
 *  - setPointerCapture keeps the stroke attached if the finger leaves the
 *    canvas mid-signature rather than ending the line abruptly.
 *  - The backing store is scaled by devicePixelRatio so the saved image isn't
 *    a blurry upscale on a retina screen.
 *  - Redrawing on resize would clear an in-progress signature, so the bitmap
 *    is re-applied after the canvas is resized.
 */
const SignaturePad = forwardRef(function SignaturePad({ onChange }, ref) {
  const canvasRef = useRef(null);
  const drawing = useRef(false);
  const last = useRef(null);
  const [empty, setEmpty] = useState(true);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const setup = () => {
      const ratio = window.devicePixelRatio || 1;
      const { width, height } = canvas.getBoundingClientRect();
      if (!width || !height) return;

      // Preserve whatever has been drawn across a resize / orientation change.
      const prev = canvas.width ? canvas.toDataURL() : null;
      canvas.width = Math.round(width * ratio);
      canvas.height = Math.round(height * ratio);

      const ctx = canvas.getContext('2d');
      ctx.setTransform(ratio, 0, 0, ratio, 0, 0);
      ctx.lineWidth = 2.5;
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';
      ctx.strokeStyle = '#18181B';

      if (prev) {
        const img = new Image();
        img.onload = () => ctx.drawImage(img, 0, 0, width, height);
        img.src = prev;
      }
    };

    setup();
    const ro = new ResizeObserver(setup);
    ro.observe(canvas);
    return () => ro.disconnect();
  }, []);

  const posOf = (e) => {
    const r = canvasRef.current.getBoundingClientRect();
    return { x: e.clientX - r.left, y: e.clientY - r.top };
  };

  const onPointerDown = (e) => {
    e.preventDefault();
    canvasRef.current.setPointerCapture?.(e.pointerId);
    drawing.current = true;
    last.current = posOf(e);
    if (empty) { setEmpty(false); onChange?.(false); }
  };

  const onPointerMove = (e) => {
    if (!drawing.current) return;
    e.preventDefault();
    const ctx = canvasRef.current.getContext('2d');
    const p = posOf(e);
    ctx.beginPath();
    ctx.moveTo(last.current.x, last.current.y);
    ctx.lineTo(p.x, p.y);
    ctx.stroke();
    last.current = p;
  };

  const stop = (e) => {
    if (!drawing.current) return;
    canvasRef.current.releasePointerCapture?.(e.pointerId);
    drawing.current = false;
    last.current = null;
  };

  useImperativeHandle(ref, () => ({
    isEmpty: () => empty,
    clear() {
      const canvas = canvasRef.current;
      canvas.getContext('2d').clearRect(0, 0, canvas.width, canvas.height);
      setEmpty(true);
      onChange?.(true);
    },
    toDataURL: () => canvasRef.current.toDataURL('image/png'),
  }), [empty, onChange]);

  return (
    <div className="sig-pad-wrap">
      <button type="button" className="sig-clear"
        onClick={() => { const c = canvasRef.current; c.getContext('2d').clearRect(0, 0, c.width, c.height); setEmpty(true); onChange?.(true); }}>
        Clear
      </button>
      <canvas
        ref={canvasRef}
        className="sig-pad"
        role="img"
        aria-label="Signature pad — draw your signature here"
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={stop}
        onPointerCancel={stop}
        onPointerLeave={stop}
      />
      <span className="sig-baseline" aria-hidden="true" />
      {empty ? <span className="sig-hint">Sign above the line</span> : null}
    </div>
  );
});

export default SignaturePad;
